/**
 * WebSocket hook for real-time clinic chat updates
 * 
 * Connects to API Gateway WebSocket for receiving new messages in real-time.
 * Gracefully handles connection failures without infinite loops.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { chatApi, ChatMessage } from '../utils/chatApi';

interface UseClinicChatOptions {
  enabled?: boolean;
  isChatOpen?: boolean;
  onNewMessage?: (message: ChatMessage) => void;
  onMessageEdited?: (message: ChatMessage) => void;
  onMessageUpdated?: (message: ChatMessage) => void;
  onMessageDeleted?: (messageId: string) => void;
  reconnectAttempts?: number;
  reconnectDelay?: number;
}

interface UseClinicChatReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  unreadCount: number;
  connect: () => void;
  disconnect: () => void;
  resetUnreadCount: () => void;
}

type WebSocketMessage = {
  type:
    | 'NEW_MESSAGE'
    | 'MESSAGE_EDITED'
    | 'MESSAGE_UPDATED'
    | 'MESSAGE_DELETED'
    | 'NOTIFICATION_NEW'
    | 'pong'
    | 'error';
  data?: any;
  message_id?: string;  // For MESSAGE_DELETED
  timestamp?: number;
  error?: string;
};

export const useClinicChat = (options: UseClinicChatOptions = {}): UseClinicChatReturn => {
  const {
    enabled = true,
    isChatOpen = false,
    onNewMessage,
    onMessageEdited,
    onMessageUpdated,
    onMessageDeleted,
    reconnectAttempts = 3,
    reconnectDelay = 5000,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasAttemptedRef = useRef(false);
  const isConnectingRef = useRef(false);
  const onNewMessageRef = useRef(onNewMessage);
  const onMessageEditedRef = useRef(onMessageEdited);
  const onMessageUpdatedRef = useRef(onMessageUpdated);
  const onMessageDeletedRef = useRef(onMessageDeleted);
  const isChatOpenRef = useRef(isChatOpen);

  // Keep refs updated without triggering reconnects
  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    onMessageEditedRef.current = onMessageEdited;
  }, [onMessageEdited]);

  useEffect(() => {
    onMessageUpdatedRef.current = onMessageUpdated;
  }, [onMessageUpdated]);

  useEffect(() => {
    onMessageDeletedRef.current = onMessageDeleted;
  }, [onMessageDeleted]);

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Check if we've exhausted reconnect attempts
    if (reconnectCountRef.current >= reconnectAttempts) {
      return;
    }

    isConnectingRef.current = true;
    setIsConnecting(true);

    try {
      // Get WebSocket URL from backend
      const { websocket_url } = await chatApi.getWebSocketUrl();
      
      // Get JWT token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Connect with token in query string
      const wsUrl = `${websocket_url}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[Chat WebSocket] Connected');
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        isConnectingRef.current = false;
        reconnectCountRef.current = 0;

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          
          if (message.type === 'NEW_MESSAGE' && message.data) {
            console.log('[Chat WebSocket] New message received:', message.data);
            // Only increment unread if chat panel is closed
            if (!isChatOpenRef.current) {
              setUnreadCount(prev => prev + 1);
            }
            onNewMessageRef.current?.(message.data);
          } else if (message.type === 'MESSAGE_EDITED' && message.data) {
            console.log('[Chat WebSocket] Message edited:', message.data);
            onMessageEditedRef.current?.(message.data);
          } else if (message.type === 'MESSAGE_UPDATED' && message.data) {
            console.log('[Chat WebSocket] Message updated:', message.data);
            onMessageUpdatedRef.current?.(message.data);
          } else if (message.type === 'MESSAGE_DELETED' && message.message_id) {
            console.log('[Chat WebSocket] Message deleted:', message.message_id);
            onMessageDeletedRef.current?.(message.message_id);
          } else if (message.type === 'NOTIFICATION_NEW' && message.data) {
            // Notifications come over the same websocket. Publish to the app without a second connection.
            try {
              window.dispatchEvent(new CustomEvent('hp_notification_new', { detail: message.data }));
            } catch (e) {
              console.warn('[Chat WebSocket] Failed to dispatch notification event', e);
            }
          }
        } catch (e) {
          console.error('[Chat WebSocket] Failed to parse message:', e);
        }
      };

      ws.onerror = () => {
        console.warn('[Chat WebSocket] Connection error');
      };

      ws.onclose = (event) => {
        console.log('[Chat WebSocket] Closed:', event.code);
        setIsConnected(false);
        setIsConnecting(false);
        isConnectingRef.current = false;
        wsRef.current = null;

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Only attempt reconnection if not intentionally closed and under limit
        if (event.code !== 1000 && reconnectCountRef.current < reconnectAttempts) {
          reconnectCountRef.current += 1;
          const delay = reconnectDelay * reconnectCountRef.current;
          
          console.log(`[Chat WebSocket] Will retry in ${delay}ms (${reconnectCountRef.current}/${reconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (e) {
      console.warn('[Chat WebSocket] Failed to connect:', e instanceof Error ? e.message : 'Unknown error');
      setError('Chat unavailable');
      setIsConnecting(false);
      isConnectingRef.current = false;

      // Schedule one retry with backoff, but don't loop forever
      reconnectCountRef.current += 1;
      if (reconnectCountRef.current < reconnectAttempts) {
        const delay = reconnectDelay * reconnectCountRef.current;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    }
  }, [reconnectAttempts, reconnectDelay]);

  const disconnect = useCallback(() => {
    reconnectCountRef.current = reconnectAttempts; // Prevent reconnects
    cleanup();
    setIsConnected(false);
    setIsConnecting(false);
    isConnectingRef.current = false;
  }, [cleanup, reconnectAttempts]);

  const resetUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Connect once on mount if enabled
  useEffect(() => {
    if (enabled && !hasAttemptedRef.current) {
      hasAttemptedRef.current = true;
      // Small delay to avoid race conditions on initial render
      const timeout = setTimeout(() => {
        connect();
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [enabled, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isConnected,
    isConnecting,
    error,
    unreadCount,
    connect,
    disconnect,
    resetUnreadCount,
  };
};

export default useClinicChat;

